import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import './Games.css';
import { GAME_REGISTRY, GameConfig, getGameCurricula } from '../models/GameRegistry';
import FilterBar, { FilterState } from './FilterBar';
import defaultGameImage from '../../../shared/assets/images/games/default-game.png';
import { gameAPI, AchievementDefinition, Achievement } from '../services/GameAPI';
import { collection, getDocs, onSnapshot, query, where } from 'firebase/firestore';
import { db } from '../../../shared/storage/firebase';
import { getGameRarity, rarityGemMap } from '../../progress/data/seasonPassData';
import { useAuth } from '../../auth/contexts/AuthContext';
import { SeasonPassRarity } from '../../progress/types/seasonPass';
import { Squad } from '../../../types';
import GameLaunchModal from './GameLaunchModal';

const Games: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { gameId: routeGameId } = useParams<{ gameId?: string }>();
  const { userData } = useAuth();
  const [previewGame, setPreviewGame] = useState<GameConfig | null>(null);
  const [gameAchievements, setGameAchievements] = useState<Record<string, AchievementDefinition[]>>({});
  const [userAchievements, setUserAchievements] = useState<Record<string, Achievement[]>>({});
  const [unlockedGames, setUnlockedGames] = useState<string[]>([]);

  // Reset preview modal when navigating explicitly to /app/games route
  useEffect(() => {
    if (location.pathname === '/app/games') {
      setPreviewGame(null);
    }
  }, [location.pathname]);

  // Students should always play games in the dedicated full-screen route.
  // This avoids the app sidebar/top tools and bypasses the in-app GameContainer view.
  useEffect(() => {
    if (!routeGameId) return;
    const activeGame = GAME_REGISTRY[routeGameId];

    // Preserve legacy behavior: if the game isn't found or is truly external, return to the collection.
    // Exception: og-creators is an internal React game that intentionally has no gameUrl.
    if (!activeGame) {
      navigate('/app/games', { replace: true });
      return;
    }

    if (activeGame.id !== 'og-creators' && isExternalUrl(activeGame.gameUrl)) {
      navigate('/app/games', { replace: true });
      return;
    }

    navigate(`/play/${routeGameId}`, { replace: true });
  }, [navigate, routeGameId]);

  // Load unlocked games based on role + live squads
  useEffect(() => {
    const ensureOgCreatorsIncluded = (ids: string[]) => {
      const set = new Set(ids);
      set.add('og-creators');
      return Array.from(set);
    };

    const allGameIds = Object.values(GAME_REGISTRY).map(game => game.id);

    // No user: show all games as unlocked
    if (!userData?.id) {
      setUnlockedGames(ensureOgCreatorsIncluded(allGameIds));
      return;
    }

    // Teachers and partners: all games unlocked
    if (userData.role === 'teacher' || userData.role === 'partner') {
      setUnlockedGames(ensureOgCreatorsIncluded(allGameIds));
      return;
    }

    // Students: base = all games when no live squad; otherwise union of unlockedGames across live squads
    if (userData.role === 'student') {
      const squadsRef = collection(db, 'squads');
      const q = query(
        squadsRef,
        where('studentIds', 'array-contains', userData.id),
        where('classStatus', '==', 'current'),
      );

      const unsubscribe = onSnapshot(q, (snapshot) => {
        const liveSquads: Squad[] = [];
        snapshot.forEach((s) => {
          const data = { id: s.id, ...s.data() } as Squad;
          // Treat squad as live only if liveSession is present
          if (data.liveSession) {
            liveSquads.push(data);
          }
        });

        if (liveSquads.length === 0) {
          // No live squads: all games unlocked, ignore Season Pass
          setUnlockedGames(ensureOgCreatorsIncluded(allGameIds));
          return;
        }

        // At least one live squad: union of each squad's unlockedGames.
        // Interpretation rules (per squad):
        // - unlockedGames is undefined              → teacher has never configured access → treat as ALL games allowed.
        // - unlockedGames is an array (incl. empty) → respect exactly (empty = all locked for that squad).
        // - unlockedGames exists but is another type → treat as "all locked" (fail closed for safety).
        const union = new Set<string>();
        liveSquads.forEach((squad) => {
          const raw = (squad as any).unlockedGames;
          let ids: string[];

          if (Array.isArray(raw)) {
            ids = raw;
          } else if (typeof raw === 'undefined') {
            const curriculumKey = String((squad as any).curriculumId || '').trim().toLowerCase();
            const isAi2026CreateWorlds = curriculumKey === 'ai-2026-create-worlds';
            ids = isAi2026CreateWorlds ? ['real-or-ai', 'og-creators'] : allGameIds;
          } else {
            ids = [];
          }

          ids.forEach((id) => union.add(id));
        });

        setUnlockedGames(ensureOgCreatorsIncluded(Array.from(union)));
      }, (error) => {
        console.error('Error loading live squads for games page:', error);
        // Fallback on error: show all games as unlocked
        setUnlockedGames(ensureOgCreatorsIncluded(allGameIds));
      });

      return () => {
        unsubscribe();
      };
    }

    // Fallback for any other roles (shouldn't normally happen): all unlocked
    setUnlockedGames(ensureOgCreatorsIncluded(allGameIds));
  }, [userData?.id, userData?.role]);

  // Load achievements for all games - reload when returning to collection view
  useEffect(() => {
    const loadAllAchievements = async () => {
      const achievementsMap: Record<string, AchievementDefinition[]> = {};
      const userAchievementsMap: Record<string, Achievement[]> = {};

      try {
        const currentUserId = await gameAPI.getCurrentUserId();

        // OPTIMIZATION 1: Load ALL user achievements ONCE (not per game)
        let allUserAchievements: Achievement[] = [];
        if (currentUserId) {
          const userAchievementsRef = collection(db, 'users', currentUserId, 'achievements');
          const userAchievementsSnapshot = await getDocs(userAchievementsRef);
          allUserAchievements = userAchievementsSnapshot.docs.map(doc => {
            const data = doc.data();
            return {
              id: doc.id,
              userId: currentUserId,
              achievementId: data.achievementId,
              unlockedAt: data.unlockedAt?.toDate() || new Date(),
              gameId: data.gameId
            } as Achievement;
          });
        }

        // OPTIMIZATION 2: Load game achievements in PARALLEL
        const games = Object.values(GAME_REGISTRY);
        const achievementPromises = games.map(game => 
          gameAPI.getGameAchievementDefinitions(game.id)
            .then(achievements => ({ gameId: game.id, achievements }))
            .catch(error => {
              console.error(`Error loading achievements for ${game.id}:`, error);
              return { gameId: game.id, achievements: [] };
            })
        );

        const results = await Promise.all(achievementPromises);

        // Process results
        results.forEach(({ gameId, achievements }) => {
          achievementsMap[gameId] = achievements.sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));
          userAchievementsMap[gameId] = allUserAchievements.filter(a => a.gameId === gameId);
        });

        setGameAchievements(achievementsMap);
        setUserAchievements(userAchievementsMap);
      } catch (error) {
        console.error('Error loading achievements:', error);
      }
    };

    // Only load/reload achievements when we're on the collection view (/app/games)
    // so that goals are refreshed after playing a game and returning.
    if (!routeGameId) {
      loadAllAchievements();
    }
  }, [routeGameId]);

  const [filters, setFilters] = useState<FilterState>({
    curriculum: 'all',
    gameMode: 'all',
    tier: 'all',
    playerCount: 'all',
    musicalElements: [],
    musicalSkills: [],
    searchTerm: '',
    category: 'all',
    elementCategory: 'all'
  });

  // Split games into collected and to discover
  const { collectedGames, gamesToDiscover } = useMemo(() => {
    const allGames = Object.values(GAME_REGISTRY).filter(g => {
      // Hide Instruments in Videos from student accounts
      const isStudent = userData?.role === 'student';
      if (isStudent && g.id === 'instruments-in-videos') return false;
      return true;
    });
    const collected = allGames.filter(game => unlockedGames.includes(game.id));
    const toDiscover = allGames.filter(game => !unlockedGames.includes(game.id));


    return {
      collectedGames: collected,
      gamesToDiscover: toDiscover
    };
  }, [unlockedGames, userData?.role]);

  // Apply filters to a game array
  const applyFilters = useCallback((games: typeof GAME_REGISTRY[keyof typeof GAME_REGISTRY][]) => {
    return games.filter(game => {
      if (filters.curriculum !== 'all' && !getGameCurricula(game.id).includes(filters.curriculum)) return false;

      // Game mode filter: Group = max >= 3, Solo = min = 1
      if (filters.gameMode === 'group') {
        if (typeof game.playerCount !== 'object' || game.playerCount.max < 3) {
          return false;
        }
      }
      if (filters.gameMode === 'solo') {
        if (typeof game.playerCount === 'object' && game.playerCount.min !== 1) {
          return false;
        }
        if (typeof game.playerCount === 'string' && game.playerCount !== 'single-player') {
          return false;
        }
      }

      // Musical elements filter (multi-select pills)
      if (filters.musicalElements.length > 0) {
        const hasAnyElement = filters.musicalElements.some(element =>
          game.musicalElements.includes(element as any)
        );
        if (!hasAnyElement) return false;
      }

      // Musical skills filter
      if (filters.musicalSkills.length > 0) {
        const hasAnySkill = filters.musicalSkills.some(skill =>
          game.musicalSkills.includes(skill as any)
        );
        if (!hasAnySkill) return false;
      }

      // Enhanced search term filter - now includes musical tags
      if (filters.searchTerm) {
        const searchLower = filters.searchTerm.toLowerCase();

        // Search in game properties
        const titleMatch = game.title.toLowerCase().includes(searchLower);
        const descriptionMatch = game.description.toLowerCase().includes(searchLower);
        const featuresMatch = game.features?.some(feature =>
          feature.toLowerCase().includes(searchLower)
        );

        // Search in musical elements and skills (tags)
        const elementMatch = game.musicalElements.some(element =>
          element.toLowerCase().includes(searchLower)
        );
        const skillMatch = game.musicalSkills.some(skill =>
          skill.toLowerCase().includes(searchLower)
        );

        // Search in category
        const categoryMatch = game.category.toLowerCase().includes(searchLower);

        // Return true if any search criteria matches
        if (!titleMatch && !descriptionMatch && !featuresMatch &&
          !elementMatch && !skillMatch && !categoryMatch) {
          return false;
        }
      }

      // Skill category filter (Performance/Improvisation/Composition/Music Theory)
      if (filters.category && filters.category !== 'all') {
        const skillLower = filters.category.toLowerCase();
        // Some registries may encode this under musicalSkills not category
        const bySkills = game.musicalSkills.some(s => s.toLowerCase() === skillLower);
        const byCategory = (game as any).category?.toLowerCase?.() === skillLower;
        if (!bySkills && !byCategory) return false;
      }

      // Element category filter (Harmony/Rhythm/Melody/Instruments/Form/Dynamics)
      if (filters.elementCategory && filters.elementCategory !== 'all') {
        const elemLower = filters.elementCategory.toLowerCase();
        const hasElement = game.musicalElements.some(e => e.toLowerCase() === elemLower);
        if (!hasElement) return false;
      }

      return true;
    });
  }, [filters]);

  // Helper function to sort games by rarity order
  const sortGamesByRarity = (games: GameConfig[]): GameConfig[] => {
    const rarityOrder: Record<SeasonPassRarity, number> = {
      common: 1,
      uncommon: 2,
      rare: 3,
      epic: 4,
      legendary: 5
    };

    return games.sort((a, b) => {
      const rarityA = getGameRarity(a.id);
      const rarityB = getGameRarity(b.id);
      // Sort rarest first
      return rarityOrder[rarityB] - rarityOrder[rarityA];
    });
  };

  // Filter and sort collected and discover games separately
  const filteredCollectedGames = useMemo(() => sortGamesByRarity(applyFilters(collectedGames)), [collectedGames, applyFilters]);
  const filteredGamesToDiscover = useMemo(() => sortGamesByRarity(applyFilters(gamesToDiscover)), [gamesToDiscover, applyFilters]);

  // Combined filtered games for backward compatibility
  const filteredGames = useMemo(() => [...filteredCollectedGames, ...filteredGamesToDiscover], [filteredCollectedGames, filteredGamesToDiscover]);

  const handleGameSelect = (game: GameConfig) => {
    // Instead of launching immediately, show the preview modal
    setPreviewGame(game);
  };

  const handleLaunchGame = async () => {
    if (!previewGame) return;

    // Navigate to the dedicated full-screen route (no sidebar, no in-app container).
    // Daily achievements are awarded inside `FullScreenGamePlayer` so the toast can render
    // in front of the game (and not get unmounted during route transitions).
    navigate(`/play/${previewGame.id}`);
    setPreviewGame(null);
  };

  const handleClosePreview = () => {
    setPreviewGame(null);
  };

  // Helper function to get rarity colors
  const getRarityColors = (rarity: SeasonPassRarity) => {
    const colors = {
      common: { border: '#22C55E', glow: 'rgba(34, 197, 94, 0.5)', gem: '#22C55E' }, // Green
      uncommon: { border: '#3B82F6', glow: 'rgba(59, 130, 246, 0.5)', gem: '#3B82F6' }, // Blue
      rare: { border: '#F97316', glow: 'rgba(249, 115, 22, 0.5)', gem: '#F97316' }, // Orange
      epic: { border: '#8B5CF6', glow: 'rgba(139, 92, 246, 0.5)', gem: '#8B5CF6' }, // Purple
      legendary: { border: '#F59E0B', glow: 'rgba(245, 158, 11, 0.5)', gem: '#F59E0B' } // Gold
    };
    return colors[rarity];
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'beta':
        return <span className="status-badge beta">🚧 Beta</span>;
      case 'premium':
        return <span className="status-badge premium">💎 Premium</span>;
      case 'coming_soon':
        return <span className="status-badge coming-soon">🔜 Coming Soon</span>;
      default:
        return null;
    }
  };

  // Tier badges are temporarily disabled – all games are considered basic for now.
  const getTierBadge = () => null;

  const isExternalUrl = (url?: string): boolean => {
    if (!url) return false;
    try {
      const isAbsolute = /^https?:\/\//i.test(url);
      if (!isAbsolute) return false; // relative paths are internal
      const target = new URL(url);
      return target.origin !== window.location.origin;
    } catch {
      return false;
    }
  };

  // Render a game card
  const renderGameCard = (game: GameConfig) => {
    const isUnlocked = unlockedGames.includes(game.id);
    const gameRarity = getGameRarity(game.id);
    const rarityColors = getRarityColors(gameRarity);

    return (
      <div
        key={game.id}
        className={`game-card ${game.status} ${isUnlocked ? 'unlocked' : 'locked'}`}
        style={{
          '--rarity-border': rarityColors.border,
          '--rarity-glow': rarityColors.glow,
          '--rarity-gem': rarityColors.gem
        } as React.CSSProperties}
        onClick={() => {
          if (!isUnlocked) return;
          if (isExternalUrl(game.gameUrl)) {
            window.open(game.gameUrl!, '_blank', 'noopener,noreferrer');
            return;
          }
          handleGameSelect(game);
        }}
      >
        <div className="game-image-container">
          <img
            src={game.thumbnail}
            alt={game.title}
            className="game-image"
            onError={(e) => {
              (e.target as HTMLImageElement).src = defaultGameImage;
            }}
          />

          {/* Lock Overlay for locked games */}
          {!isUnlocked && (
            <div className="game-lock-overlay">
              <div className="lock-icon">🔒</div>
              <div className="lock-text">Unlock via Season Pass</div>
            </div>
          )}

          {game.like && (
            <div className="like-banner">
              <span className="like-banner-text">LIKE: {game.like}</span>
            </div>
          )}

          <div className="game-badges">
            {getTierBadge()}
            {getStatusBadge(game.status)}
          </div>

          {/* Rarity Gem */}
          <div className="rarity-gem">
            <img
              src={rarityGemMap[gameRarity]}
              alt={`${gameRarity} rarity`}
              className="gem-image"
            />
          </div>

          {/* Rarity Label */}
          <div className="rarity-label">
            {gameRarity.toUpperCase()}
          </div>
        </div>

        <div className="game-info">
          <div className="game-header">
            <h3 className="game-title-blue">{game.title}</h3>
          </div>

          {(() => {
            const achievementData = getAchievementTeasers(game.id);

            if (achievementData.message) {
              return (
                <div className="achievement-status-message">
                  {achievementData.message}
                </div>
              );
            }

            return (
              <div className="achievement-section">
                {/* Today's Goals - Show when NOT all complete */}
                {!achievementData.allDailyComplete && achievementData.dailyAchievements.length > 0 && (
                    <div className="todays-goals">
                      <div className="todays-goals-header">🎯Today's Goals</div>
                      <div className="daily-achievements-list">
                        {achievementData.dailyAchievements.map(achievement => (
                          <div
                            key={achievement.id}
                            className={`daily-achievement-item ${achievement.isCompleted ? 'completed' : ''}`}
                          >
                            <span className="daily-achievement-text">
                              {achievement.isCompleted ? '✅\u00A0\u00A0' : '⭕\u00A0\u00A0'}
                              {achievement.title}
                            </span>
                            <span className="daily-achievement-reward">
                              +{achievement.museCashReward}💰
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                )}

                {/* Today's Goals Complete - Show when all daily complete */}
                {achievementData.allDailyComplete && (
                  <div className="todays-goals">
                    <div className="todays-goals-header todays-goals-complete">
                      🎯Today's Goals COMPLETE
                    </div>
                    
                    {/* All-Time Progress */}
                    <div className="all-time-progress">
                      <div className="progress-bar">
                        <div
                          className="progress-fill"
                          style={{
                            width: `${(achievementData.allTimeProgress.completed / achievementData.allTimeProgress.total) * 100}%`
                          }}
                        ></div>
                      </div>
                      <div className="progress-text">
                        All-Time: {achievementData.allTimeProgress.completed}/{achievementData.allTimeProgress.total} achievements
                      </div>
                    </div>

                    {/* Next Unlocked Achievement */}
                    {achievementData.nextUnlockedAchievement && (
                      <div className="daily-achievements-list">
                        <div className="daily-achievement-item">
                          <span className="daily-achievement-text">
                            {'⭕\u00A0\u00A0'}
                            {achievementData.nextUnlockedAchievement.title}
                          </span>
                          <span className="daily-achievement-reward">+{achievementData.nextUnlockedAchievement.museCashReward}💰</span>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })()}
        </div>
      </div>
    );
  };

  const getAchievementTeasers = (gameId: string) => {
    const achievements = gameAchievements[gameId] || [];
    const userCompleted = userAchievements[gameId] || [];

    if (achievements.length === 0) {
      return { 
        dailyAchievements: [], 
        allTimeProgress: { completed: 0, total: 0 },
        allDailyComplete: false,
        nextUnlockedAchievement: null,
        message: "Loading achievements..." 
      };
    }

    // Separate daily achievements from all achievements
    const dailyAchievements = achievements.filter(a => a.type === 'daily');
    const nonDailyAchievements = achievements.filter(a => a.type !== 'daily');
    
    // Check which daily achievements are completed today
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const dailyStatus = dailyAchievements.map(achievement => {
      const userAchievement = userCompleted.find(ua => ua.achievementId === achievement.id);
      const isCompletedToday = userAchievement && 
        new Date(userAchievement.unlockedAt).setHours(0, 0, 0, 0) === today.getTime();
      
      return {
        ...achievement,
        isCompleted: isCompletedToday
      };
    });

    // Check if all daily goals are complete
    const allDailyComplete = dailyStatus.length > 0 && dailyStatus.every(d => d.isCompleted);

    // Find the smallest musecash achievement that hasn't been unlocked yet
    const unlockedAchievements = nonDailyAchievements.filter(achievement =>
      !userCompleted.some(ua => ua.achievementId === achievement.id)
    );
    
    const nextUnlockedAchievement = unlockedAchievements.length > 0
      ? unlockedAchievements.sort((a, b) => a.museCashReward - b.museCashReward)[0]
      : null;

    return {
      dailyAchievements: dailyStatus,
      allTimeProgress: {
        completed: userCompleted.length,
        total: achievements.length
      },
      allDailyComplete,
      nextUnlockedAchievement,
      message: null
    };
  };


  // If a gameId is present in the route, render the in-game view
  if (routeGameId) {
    // Everyone should be redirected to /play/:gameId via the effect above.
    // While that redirect is happening, show a lightweight loading state.
    return <div className="loading">Loading game...</div>;
  }

  return (
    <div className="games-page">
      <div className="games-header">
        <h1 className="games-title-blue">Games Collection</h1>
        <p className="games-subtitle">Choose a curriculum, then find the right game for your class.</p>
      </div>

      <FilterBar
        filters={filters}
        onFilterChange={setFilters}
        compact
      />

      <div className="games-results">
        {/* Collected Games Section */}
        {filteredCollectedGames.length > 0 && (
          <div className="games-section">
            <div className="section-header">
              <h2 className="section-title">
                Showing {filteredCollectedGames.length} of {collectedGames.length} collected games
              </h2>
            </div>

            <div className="games-grid">
              {filteredCollectedGames.map(renderGameCard)}
            </div>
          </div>
        )}

        {/* Games to Discover Section */}
        {filteredGamesToDiscover.length > 0 && (
          <div className="games-section">
            <div className="section-header">
              <h2 className="section-title">
                🔍 {filteredGamesToDiscover.length} Games to Discover
              </h2>
            </div>

            <div className="games-grid">
              {filteredGamesToDiscover.map(renderGameCard)}
            </div>
          </div>
        )}

        {filteredGames.length === 0 && (
          <div className="no-results">
            <h3>🔍 No games found</h3>
            <p>Try adjusting your filters or search terms to find more games!</p>
          </div>
        )}
      </div>

      {/* Game Launch Modal */}
      {previewGame && (
        <GameLaunchModal
          game={previewGame}
          onPlay={handleLaunchGame}
          onClose={handleClosePreview}
          achievements={gameAchievements[previewGame.id] || []}
          userAchievements={userAchievements[previewGame.id] || []}
        />
      )}
    </div>
  );
};

export default Games;
